import React, { useState, useMemo, useEffect } from "react";
import {
  Flex,
  Spin,
  Tooltip,
  Row,
  Col,
  Checkbox,
  Input,
  List,
  Typography,
  Space,
  Button,
  Card,
  Empty,
  Tag,
  message,
  Select,
  Alert,
} from "antd";
import {
  SearchOutlined,
  ShoppingOutlined,
  AppstoreOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  WsProduct,
  WsProductVariant,
  useGetWsProductWithVariantsQuery,
  useBulkUpdateVariantsMutation,
  useUpdateWsVariantMutation,
  useDeleteWsVariantMutation,
} from "../../../api/webshop/wsProductsApi";

const { Text, Title, Paragraph } = Typography;

export interface WsProductExtended extends WsProduct {
  variantIds?: string[];
}

export default function VariantTransferTab(props: {
  p: WsProductExtended;
  setP: (p: WsProductExtended) => void;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [activeVariantId, setActiveVariantId] = useState<string | null>(null);

  // Local state for editing the active variant details
  const [editingVariant, setEditingVariant] = useState<WsProductVariant | null>(
    null,
  );
  const [stockBeforeOutOfStock, setStockBeforeOutOfStock] = useState(0);

  // Fetch the product details and its variants
  const { data, isLoading, error, refetch } = useGetWsProductWithVariantsQuery(
    props.p.productId,
    { skip: !props.p.productId },
  );

  const [bulkUpdateVariants] = useBulkUpdateVariantsMutation();
  const [updateVariant, { isLoading: isSavingVariant }] =
    useUpdateWsVariantMutation();
  const [deleteVariant] = useDeleteWsVariantMutation();
  const variants = data?.variants || [];

  // Default the active variant to the first item when data is loaded
  useEffect(() => {
    if (variants.length > 0 && !activeVariantId) {
      setActiveVariantId(variants[0].variantId);
    }
  }, [variants, activeVariantId]);

  const activeVariant = useMemo(() => {
    return variants.find((v) => v.variantId === activeVariantId) || null;
  }, [variants, activeVariantId]);

  // Sync editing variant state when active variant changes
  useEffect(() => {
    if (activeVariant) {
      setEditingVariant({ ...activeVariant });
      const existingStock = activeVariant.stock;
      setStockBeforeOutOfStock(
        existingStock != null && existingStock !== -1 ? existingStock : 0,
      );
    } else {
      setEditingVariant(null);
      setStockBeforeOutOfStock(0);
    }
  }, [activeVariant]);

  const filteredVariants = useMemo(() => {
    const sorted = [...variants].sort((a, b) =>
      (a.internalSku ?? a.variantId ?? "").localeCompare(
        b.internalSku ?? b.variantId ?? "",
        undefined,
        { sensitivity: "base" },
      ),
    );
    if (!searchQuery.trim()) return sorted;
    const q = searchQuery.toLowerCase().trim();
    return sorted.filter(
      (v) =>
        (v.name && v.name.toLowerCase().includes(q)) ||
        (v.variantId && v.variantId.toLowerCase().includes(q)) ||
        (v.internalSku && v.internalSku.toLowerCase().includes(q)),
    );
  }, [variants, searchQuery]);

  // Selection Checkbox handlers
  const handleCheckItem = (id: string, checked: boolean) => {
    if (checked) {
      setCheckedIds([...checkedIds, id]);
    } else {
      setCheckedIds(checkedIds.filter((item) => item !== id));
    }
  };

  const isAllChecked =
    filteredVariants.length > 0 &&
    checkedIds.length === filteredVariants.length;
  const isIndeterminate =
    checkedIds.length > 0 && checkedIds.length < filteredVariants.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setCheckedIds(filteredVariants.map((v) => v.variantId));
    } else {
      setCheckedIds([]);
    }
  };

  // Bulk action dispatcher
  const handleBulkAction = (action: string) => {
    console.log(`Executing bulk ${action} on variants:`, checkedIds);
    bulkUpdateVariants({
      action,
      variantIds: checkedIds,
      productId: props.p.productId,
    })
      .unwrap()
      .then(() => {
        message.success(`Bulk action '${action}' completed successfully.`);
        setCheckedIds([]);
        refetch();
      })
      .catch((err) => {
        console.error("Bulk update failure:", err);
        message.error(
          `Bulk action '${action}' failed (Logged payload to console).`,
        );
        setCheckedIds([]);
      });
  };

  // Save changes to individual variant
  const handleSaveVariant = () => {
    if (!editingVariant) return;
    console.log("Saving individual variant details:", editingVariant);

    updateVariant(editingVariant)
      .unwrap()
      .then(() => {
        message.success("Variant details saved successfully.");
        refetch();
      })
      .catch((err) => {
        console.error("Update variant error:", err);
        message.error("Error saving variant. (Logged payload to console)");
      });
  };

  const handleDeleteVariant = () => {
    if (!editingVariant) return;
    console.log("Deleting variant:", editingVariant);
    deleteVariant({
      variantId: editingVariant.variantId,
      productId: props.p.productId,
    })
      .unwrap()
      .then(() => {
        message.success("Variant deleted successfully.");
        setEditingVariant(null);
        refetch();
      })
      .catch((err) => {
        console.error("Delete variant error:", err);
        message.error("Error deleting variant. (Logged payload to console)");
      });
  };

  if (!props.p.productId) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: "300px" }}>
        <Empty description="Save the product details first to configure variants." />
      </Flex>
    );
  }

  if (isLoading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: "300px" }}>
        <Spin size="large" tip="Loading variants list..." />
      </Flex>
    );
  }

  return (
    <div style={{ padding: "8px 0" }}>
      {error && (
        <Alert
          message="Failed to load variants"
          type="error"
          showIcon
          style={{ marginBottom: "16px" }}
        />
      )}

      <Row gutter={16}>
        {/* Left Column - Variants List */}
        <Col span={10}>
          <Flex vertical gap={8}>
            <Input
              placeholder="Search variants..."
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />

            {/* Bulk actions block above variants */}
            {checkedIds.length > 0 && (
              <Flex
                align="center"
                justify="space-between"
                style={{
                  background: "rgba(255, 70, 37, 0.05)",
                  border: "1px solid rgb(255, 70, 37)",
                  padding: "8px 12px",
                  borderRadius: "6px",
                }}
              >
                <Text strong style={{ color: "rgb(255, 70, 37)" }}>
                  {checkedIds.length} Selected
                </Text>
                <Space>
                  <Button
                    size="small"
                    type="primary"
                    style={{
                      backgroundColor: "#237804",
                      borderColor: "#237804",
                    }}
                    onClick={() => handleBulkAction("ACTIVATE")}
                  >
                    Activate
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    danger
                    onClick={() => handleBulkAction("DEACTIVATE")}
                  >
                    Deactivate
                  </Button>
                </Space>
              </Flex>
            )}

            {/* Scrollable list container */}
            <Card
              bodyStyle={{ padding: 0 }}
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              {/* Select All Checkbox Header */}
              <div
                style={{
                  padding: "10px 16px",
                  borderBottom: "1px solid #f0f0f0",
                  background: "#fafafa",
                }}
              >
                <Checkbox
                  checked={isAllChecked}
                  indeterminate={isIndeterminate}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  <Text strong>Select All Variants</Text>
                </Checkbox>
              </div>

              {/* Scrollable checklist */}
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                <List
                  dataSource={filteredVariants}
                  renderItem={(item: WsProductVariant) => {
                    const isChecked = checkedIds.includes(item.variantId);
                    const isActive = activeVariantId === item.variantId;
                    return (
                      <List.Item
                        onClick={() => setActiveVariantId(item.variantId)}
                        style={{
                          cursor: "pointer",
                          background: isActive
                            ? "rgba(255, 70, 37, 0.05)"
                            : "transparent",
                          padding: "10px 16px",
                          borderBottom: "1px solid #f5f5f5",
                          transition: "background 0.3s",
                        }}
                      >
                        <Flex align="center" gap={12} style={{ width: "100%" }}>
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                              e.stopPropagation(); // prevent triggering active row selection
                              handleCheckItem(item.variantId, e.target.checked);
                            }}
                          />
                          <Flex vertical style={{ flex: 1 }}>
                            <Text
                              strong
                              style={{
                                color: isActive
                                  ? "rgb(255, 70, 37)"
                                  : "inherit",
                              }}
                            >
                              {item.name}
                            </Text>
                            <Text type="secondary" style={{ fontSize: "11px" }}>
                              SKU: {item.internalSku || item.variantId}
                            </Text>
                          </Flex>
                          <Tag
                            color={
                              item.status === "ACTIVE" ? "green" : "volcano"
                            }
                          >
                            {item.status}
                          </Tag>
                        </Flex>
                      </List.Item>
                    );
                  }}
                  locale={{ emptyText: "No variants found." }}
                />
              </div>
            </Card>
          </Flex>
        </Col>

        {/* Right Column - Detail Editor */}
        <Col span={14}>
          {editingVariant ? (
            <Card
              title={
                <Flex align="center" gap={8}>
                  <AppstoreOutlined style={{ color: "rgb(255, 70, 37)" }} />
                  <Text strong>Edit Variant Details</Text>
                </Flex>
              }
              style={{
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                borderRadius: "6px",
              }}
            >
              <Flex vertical gap={12}>
                <Input
                  addonBefore="Variant ID"
                  disabled
                  value={editingVariant.variantId}
                />
                <Input
                  addonBefore="Variant Name"
                  value={editingVariant.name}
                  onChange={(e) =>
                    setEditingVariant({
                      ...editingVariant,
                      name: e.target.value,
                    })
                  }
                />
                <Input
                  addonBefore="Data Plan"
                  value={editingVariant.dataPlan}
                  onChange={(e) =>
                    setEditingVariant({
                      ...editingVariant,
                      dataPlan: e.target.value,
                    })
                  }
                />
               <Flex gap={8} align="start">
               <Input
                  addonBefore="Internal SKU"
                  value={editingVariant.internalSku || ""}
                  onChange={(e) =>
                    setEditingVariant({
                      ...editingVariant,
                      internalSku: e.target.value,
                    })
                  }
                  style={{ flex: 2 }}
                />
                 <Flex vertical gap={8} style={{ flex: 1 }}>
                   <Input
                      addonBefore="Stock"
                      type="number"
                      value={editingVariant.stock}
                      disabled={editingVariant.stock === -1}
                      onChange={(e) => {
                        const parsed = parseInt(e.target.value, 10);
                        const nextStock = Number.isNaN(parsed) ? 0 : parsed;
                        if (nextStock !== -1) {
                          setStockBeforeOutOfStock(nextStock);
                        }
                        setEditingVariant({
                          ...editingVariant,
                          stock: nextStock,
                        });
                      }}
                    />
                    <Checkbox
                      checked={editingVariant.stock === -1}
                      onChange={(e) => {
                        if (e.target.checked) {
                          if (
                            editingVariant.stock != null &&
                            editingVariant.stock !== -1
                          ) {
                            setStockBeforeOutOfStock(editingVariant.stock);
                          }
                          setEditingVariant({
                            ...editingVariant,
                            stock: -1,
                          });
                        } else {
                          setEditingVariant({
                            ...editingVariant,
                            stock:
                              stockBeforeOutOfStock >= 0
                                ? stockBeforeOutOfStock
                                : 0,
                          });
                        }
                      }}
                    >
                      Mark Out of Stock
                    </Checkbox>
                 </Flex>
               </Flex>
                <Flex gap={8}>
                  <Input
                    addonBefore="Price ($)"
                    type="number"
                    value={editingVariant.price}
                    onChange={(e) =>
                      setEditingVariant({
                        ...editingVariant,
                        price: parseFloat(e.target.value) || 0,
                      })
                    }
                    style={{ flex: 1 }}
                  />
                     <Input
                    addonBefore="Calls"
                    type="string"
                    value={editingVariant.calls || ""}
                    onChange={(e) =>
                      setEditingVariant({
                        ...editingVariant,
                        calls: e.target.value,
                      })
                    }
                    style={{ flex: 1 }}
                  />
                 
                </Flex>
                <Flex gap={8}>
                  <Input
                    addonBefore="Duration (Days)"
                    type="number"
                    value={editingVariant.duration}
                    onChange={(e) =>
                      setEditingVariant({
                        ...editingVariant,
                        duration: parseInt(e.target.value) || 0,
                      })
                    }
                    style={{ flex: 1 }}
                  />
                  <Input
                    addonBefore="Data Allowance"
                    value={editingVariant.dataGb || ""}
                    onChange={(e) =>
                      setEditingVariant({
                        ...editingVariant,
                        dataGb: e.target.value,
                      })
                    }
                    style={{ flex: 1 }}
                  />
                </Flex>
                <Input
                  addonBefore="Promotional Tag"
                  value={editingVariant.promotionalTag || ""}
                  onChange={(e) =>
                    setEditingVariant({
                      ...editingVariant,
                      promotionalTag: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="e.g. BEST SELLER"
                />
                <Input
                  addonBefore="Bonus Data"
                  value={editingVariant.bonusData || ""}
                  onChange={(e) =>
                    setEditingVariant({
                      ...editingVariant,
                      bonusData: e.target.value,
                    })
                  }
                  placeholder="e.g. Bonus 3GB if activated by 30 July."
                  style={{ flex: 2 }}
                />
                <Input
                  addonBefore="Operator / Carrier"
                  value={editingVariant.operator || ""}
                  onChange={(e) =>
                    setEditingVariant({
                      ...editingVariant,
                      operator: e.target.value,
                    })
                  }
                />
                <Flex gap={8} align="center">
                  <Text style={{ minWidth: "120px" }}>eKYC Required</Text>
                  <Input
                    value={editingVariant.eKyc || ""}
                    onChange={(e) =>
                      setEditingVariant({
                        ...editingVariant,
                        eKyc: e.target.value,
                      })
                    }
                  />
                </Flex>
                <Flex gap={8} align="center">
                  <Text style={{ minWidth: "120px" }}>Status</Text>
                  <Select
                    value={editingVariant.status || "ACTIVE"}
                    onChange={(v) =>
                      setEditingVariant({ ...editingVariant, status: v })
                    }
                    options={[
                      { label: "ACTIVE", value: "ACTIVE" },
                      { label: "INACTIVE", value: "INACTIVE" },
                    ]}
                    style={{ flex: 1 }}
                  />
                </Flex>
                <div>
                  <Text style={{ display: "block", marginBottom: "4px" }}>
                    Product Description
                  </Text>
                  <Input.TextArea
                    rows={3}
                    value={editingVariant.description || ""}
                    onChange={(e) =>
                      setEditingVariant({
                        ...editingVariant,
                        description: e.target.value,
                      })
                    }
                    placeholder="Enter Product Description..."
                    style={{ borderRadius: "6px" }}
                  />
                </div>
                <div>
                  <Text style={{ display: "block", marginBottom: "4px" }}>
                    Validity Scope Notes
                  </Text>
                  <Input.TextArea
                    rows={3}
                    value={
                      (editingVariant.validity &&
                        (typeof editingVariant.validity === "string"
                          ? editingVariant.validity
                          : editingVariant.validity?.description || "")) ||
                      ""
                    }
                    onChange={(e) =>
                      setEditingVariant({
                        ...editingVariant,
                        validity: e.target.value,
                      })
                    }
                    placeholder="Enter validity rules..."
                    style={{ borderRadius: "6px" }}
                  />
                </div>
                <div>
                  <Text style={{ display: "block", marginBottom: "4px" }}>
                    Activation Policy
                  </Text>
                  <Input.TextArea
                    rows={3}
                    value={editingVariant.activationPolicy || ""}
                    onChange={(e) =>
                      setEditingVariant({
                        ...editingVariant,
                        activationPolicy: e.target.value,
                      })
                    }
                    placeholder="Enter Product Description..."
                    style={{ borderRadius: "6px" }}
                  />
                </div>
                <Flex justify="space-between" style={{ marginTop: "8px" }}>
                  <Button
                    type="text"
                    onClick={handleDeleteVariant}
                    style={{ color: "red" }}
                  >
                    <DeleteOutlined />
                    Delete Variant
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSaveVariant}
                    loading={isSavingVariant}
                    style={{
                      backgroundColor: "rgb(255, 70, 37)",
                      borderColor: "rgb(255, 70, 37)",
                    }}
                  >
                    Save Variant
                  </Button>
                </Flex>
              </Flex>
            </Card>
          ) : (
            <Card
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px dashed #d9d9d9",
              }}
            >
              <Empty description="Select a variant from the list to edit its properties." />
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
